import { useToken } from 'chakra-ui-next'
import { chakra } from '../chakra'
import type { HTMLChakraProps } from '../chakra'
import { useColorMode } from '../components/color-mode'
import * as React from 'react'

export interface VESYLLogoProps extends HTMLChakraProps<'svg'> {
  iconColor?: string
  textColor?: string
  variant?: 'full' | 'logomark'
  /**
   * When true, uses black (light mode) or white (dark mode) for all colors.
   * Overrides iconColor and textColor.
   */
  monochrome?: boolean
}

const VESYLLogoSvg = React.forwardRef<
  SVGSVGElement,
  {
    iconColor?: string
    textColor?: string
    variant?: 'full' | 'logomark'
    monochrome?: boolean
  } & React.SVGProps<SVGSVGElement>
>(function VESYLLogoSvg(
  { iconColor, textColor, variant = 'full', monochrome = false, ...rest },
  ref,
) {
  const { colorMode } = useColorMode()
  const brandColor = useToken('colors', 'brand')
  const fgColor = useToken('colors', 'fg')

  const defaultIconColor = Array.isArray(brandColor) ? brandColor[0] : brandColor
  const defaultTextColor = Array.isArray(fgColor) ? fgColor[0] : fgColor

  // If monochrome is enabled, use black/white based on color mode
  const monochromeColor = colorMode === 'dark' ? '#FFFFFF' : '#000000'
  const finalIconColor = monochrome ? monochromeColor : (iconColor ?? defaultIconColor)
  const finalTextColor = monochrome ? monochromeColor : (textColor ?? defaultTextColor)

  const sharedProps = {
    clipRule: 'evenodd' as const,
    fillRule: 'evenodd' as const,
    strokeLinejoin: 'round' as const,
    strokeMiterlimit: '2' as const,
    xmlns: 'http://www.w3.org/2000/svg' as const,
  }

  // Logomark only (just the arrow icon)
  if (variant === 'logomark') {
    return (
      <svg ref={ref} viewBox="0 0 512 512" fill={finalIconColor} {...sharedProps} {...rest}>
        <g fillRule="nonzero" transform="matrix(2.11684 0 0 2.11684 0 76.661347)">
          <path d="m161.08 3.58h-80.06l-81.02 81.02 81.26 81.26h79.82l-80.78-80.79z" />
          <path d="m161.08 3.58v162.28l80.79-80.79z" />
        </g>
      </svg>
    )
  }

  // Full logo with icon and text
  return (
    <svg ref={ref} viewBox="0 0 1000 170" {...sharedProps} {...rest}>
      <g fillRule="nonzero">
        <path
          d="m161.08 3.58h-80.06l-81.02 81.02 81.26 81.26h79.82l-80.78-80.79z"
          fill={finalIconColor}
        />
        <path d="m161.08 3.58v162.28l80.79-80.79z" fill={finalIconColor} />
        <g fill={finalTextColor}>
          <path d="m489.04 68.25h72.07v34.36h-72.07v28.16h82.33v35.08h-122.66v-162.27h122.66v35.08h-82.33z" />
          <path d="m717.83 53.94h-40.57c-2.15-12.17-9.54-21.96-26.73-21.96-14.32 0-23.62 6.21-23.62 16.7 0 9.31 6.68 13.36 19.57 15.99l22.67 5.25c29.83 6.44 48.92 18.61 48.92 46.53 0 32.45-26.73 52.74-67.3 52.74-36.51 0-69.44-17.66-73.26-57.75h40.57c2.62 14.32 14.8 23.63 33.89 23.63 14.31 0 22.91-5.73 22.91-14.8 0-5.73-3.1-11.69-17.18-14.32l-26.73-5.73c-31.98-6.92-47.01-21.48-47.01-47.25-.01-32.45 26.25-52.97 64.43-52.97 26.25 0 65.39 10.26 69.44 53.94z" />
          <path d="m810.23 116.94v48.92h-40.57v-48.92l-64.19-113.36h44.63l39.62 70.64 39.85-70.64h44.63z" />
          <path d="m1000 165.86h-120.51v-162.28h40.33v126.72h80.18z" />
          <path d="m372.59 165.86 69.75-162.28h-44.39l-46.29 110.02-46.3-110.02h-44.39l69.99 162.28z" />
        </g>
      </g>
    </svg>
  )
})

export const VESYLLogo = React.forwardRef<SVGSVGElement, VESYLLogoProps>(
  function VESYLLogo(props, ref) {
    const { iconColor, textColor, variant, monochrome, ...chakraProps } = props

    return (
      <chakra.svg asChild {...chakraProps} ref={ref}>
        <VESYLLogoSvg
          iconColor={iconColor}
          textColor={textColor}
          variant={variant}
          monochrome={monochrome}
        />
      </chakra.svg>
    )
  },
)

// Legacy export for backward compatibility
export const VesylLogo = VESYLLogo
